import { Component } from '@theme/component';
import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * A page menu that lifts out into a floating pill under the header once it is scrolled past. Used
 * by the collection's filter row; the edit pages' thread menu does the same in
 * `product-picker-nav.js`, and both share the look in `assets/brand.css`, section 8.
 *
 * Two parts in the markup: a 1px sentinel, which stays in the flow, and the bar, which goes
 * `position: fixed` when stuck. `position: sticky` was not enough: the filter row's parent is only
 * as tall as its header, so a sticky row would ride for a few pixels and leave with it.
 *
 * **The in-page height is measured only while not stuck** (`--subnav-flow-height`, on this
 * element, which the host takes as its height once the bar leaves). The floating pill is shorter,
 * and re-measuring it would shrink the placeholder, move the page, unstick the bar and grow it
 * again: the loop that made the edit pages judder on a slow scroll.
 *
 * @typedef {Object} StickyPillRefs
 * @property {HTMLElement} sentinel
 * @property {HTMLElement} bar
 *
 * @extends {Component<StickyPillRefs>}
 */
class StickyPill extends Component {
  requiredRefs = ['sentinel', 'bar'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#publishHeight();
    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(this.refs.bar);

    // Stuck once the sentinel has passed above the top of the scrollport, not merely when it is
    // out of view: below the fold it is out of view too.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const top = entry.rootBounds?.top ?? 0;
        this.#setStuck(!entry.isIntersecting && entry.boundingClientRect.top < top);
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );
    this.#observer.observe(this.refs.sentinel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    document.documentElement.style.removeProperty('--bk-subnav-height');
  }

  /**
   * @param {boolean} stuck
   * @returns {void}
   */
  #setStuck(stuck) {
    if (stuck === this.hasAttribute('data-stuck')) return;
    this.toggleAttribute('data-stuck', stuck);
    this.#publishHeight();
  }

  /**
   * `--bk-subnav-height` on the document is the bar's height now, for the item panel's room and
   * scroll margin; neither moves the sentinel, so it cannot feed back.
   *
   * @returns {void}
   */
  #publishHeight() {
    const height = this.refs.bar.getBoundingClientRect().height;
    if (!height) return;

    const px = `${Math.round(height)}px`;
    if (!this.hasAttribute('data-stuck')) this.style.setProperty('--subnav-flow-height', px);
    document.documentElement.style.setProperty('--bk-subnav-height', this.hasAttribute('data-stuck') ? px : '0px');
  }
}

if (!customElements.get('sticky-pill')) {
  customElements.define('sticky-pill', StickyPill);
}
